import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { motion, useInView } from "framer-motion";
import Header from "./Header";
import Footer from "./Footer";
import { useLanguage } from "./LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import hero_picture from "./hero.png";
import record from "./record.png";
import upload from "./upload.png";
import analysis from "./analysis.png";
import MentorIcon from "./logo.png";
import Prototype from "./Pasted Graphic.png";

/* ─── Reusable reveal hook ─── */
function FadeUp({ children, delay = 0, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      style={style}
    >
      {children}
    </motion.div>
  );
}


/* ─── Component ─── */
const Home = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <PageContainer>
      <Header />

      {/* ── HERO ── */}
      <Hero>
        <HeroOverlay />
        <Blob style={{ top: "-10%", right: "28%", width: 600, height: 600, opacity: 0.18 }} />
        <Blob style={{ top: "40%", right: "-5%", width: 500, height: 500, opacity: 0.12, animationDelay: "-3s" }} />
        <Blob style={{ top: "55%", left: "5%", width: 380, height: 380, opacity: 0.09, animationDelay: "-6s" }} />
        <HeroContent>
          <FadeUp delay={0.1}>
            <HeroEyebrow>{t("home.heroEyebrow")}</HeroEyebrow>
          </FadeUp>
          <FadeUp delay={0.22}>
            <HeroTitle>
              {t("home.heroTitle")}{" "}
              <span style={{ color: "#4CAF50" }}>{t("home.heroTitleAccent")}</span>
            </HeroTitle>
          </FadeUp>
          <FadeUp delay={0.34}>
            <HeroSub>
              {t("home.heroSub")}{" "}
              <strong>{t("home.heroSubStrong")}</strong>
            </HeroSub>
          </FadeUp>
          <FadeUp delay={0.46}>
            <GreenButton onClick={() => navigate("/premium")}>
              {t("home.heroBtn")}
            </GreenButton>
          </FadeUp>
        </HeroContent>
        <FadeUp delay={0.3} style={{ position: "relative", zIndex: 1, flexShrink: 0 }}>
          <HeroImage src={hero_picture} alt="Golf swing" />
        </FadeUp>
      </Hero>


      {/* ── HOW IT WORKS ── */}
      <Section style={{ background: "#f0f7f0" }}>
        <FadeUp>
          <SectionEyebrow>{t("home.howEyebrow")}</SectionEyebrow>
          <SectionTitle>{t("home.howTitle")}</SectionTitle>
        </FadeUp>
        <StepsGrid>
          {[record, upload, analysis].map((img, i) => {
            const step = t("home.steps")[i];
            return (
              <FadeUp key={i} delay={i * 0.15}>
                <StepCard>
                  <StepNum>{String(i + 1).padStart(2, "0")}</StepNum>
                  <StepImg src={img} alt={step.label} />
                  <StepLabel>{step.label}</StepLabel>
                  <StepDesc>{step.desc}</StepDesc>
                </StepCard>
              </FadeUp>
            );
          })}
        </StepsGrid>
      </Section>

      {/* ── FEATURES GRID ── */}
      <Section style={{ background: "#fff" }}>
        <FadeUp>
          <SectionEyebrow>{t("home.whyEyebrow")}</SectionEyebrow>
          <SectionTitle>{t("home.whyTitle")}</SectionTitle>
        </FadeUp>
        <FeaturesGrid>
          {["🎯","🤖","📊","🏌️","📱","⚡"].map((icon, i) => {
            const f = t("home.features")[i];
            return (
              <FadeUp key={i} delay={(i % 3) * 0.12} style={{ height: "100%" }}>
                <FeatureCard style={{ height: "100%" }}>
                  <FeatureIcon>{icon}</FeatureIcon>
                  <FeatureTitle>{f.title}</FeatureTitle>
                  <FeatureDesc>{f.desc}</FeatureDesc>
                </FeatureCard>
              </FadeUp>
            );
          })}
        </FeaturesGrid>
      </Section>

      {/* ── DOWNLOAD CTA ── */}
      <Section style={{ background: "#f0f7f0" }}>
        <CtaRow>
          <FadeUp style={{ flex: 1 }}>
            <SectionEyebrow>{t("home.downloadEyebrow")}</SectionEyebrow>
            <CtaTitle>{t("home.downloadTitle").split("\n").map((l,i) => <span key={i}>{l}<br /></span>)}</CtaTitle>
            <CtaSub>{t("home.downloadSub")}</CtaSub>
            <GreenButton>
              {t("home.downloadBtn")} <FontAwesomeIcon icon={faArrowDown} style={{ marginLeft: 8 }} />
            </GreenButton>
          </FadeUp>
          <FadeUp delay={0.2} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <motion.img
              src={Prototype}
              alt="app mockup"
              whileHover={{ scale: 1.04, rotate: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              style={{ maxHeight: 480, borderRadius: 24, objectFit: "contain" }}
            />
          </FadeUp>
        </CtaRow>
      </Section>

      {/* ── ABOUT ── */}
      <Section style={{ background: "#fff" }}>
        <AboutRow>
          <FadeUp delay={0.15} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <AboutLogoWrap>
              <AboutLogoBlob />
              <motion.img
                src={MentorIcon}
                alt="SwingMentor logo"
                whileHover={{ scale: 1.04 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                style={{ position: "relative", zIndex: 1, maxWidth: 320, width: "100%", borderRadius: 24 }}
              />
            </AboutLogoWrap>
          </FadeUp>
          <FadeUp style={{ flex: 1 }}>
            <SectionEyebrow>{t("home.aboutEyebrow")}</SectionEyebrow>
            <AboutTitle>
              {t("home.aboutTitle")} <span style={{ color: "#4CAF50" }}>{t("home.aboutTitleAccent")}</span>
            </AboutTitle>
            <AboutText>{t("home.aboutText")}</AboutText>
            <OutlineButton onClick={() => navigate("/about")}>{t("home.aboutBtn")}</OutlineButton>
          </FadeUp>
        </AboutRow>
      </Section>

      <Footer />
    </PageContainer>
  );
};


/* ─── Styled Components ─── */
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  color: #111;
  overflow-x: hidden;
  font-family: 'Montserrat', sans-serif;
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1);    opacity: 1; }
  50%       { transform: scale(1.15); opacity: 0.7; }
`;

const logoPulse = keyframes`
  0%, 100% { transform: scale(1) translate(-50%, -50%);    opacity: 0.5; }
  50%       { transform: scale(1.2) translate(-41%, -41%);  opacity: 0.72; }
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(76,175,80,0.9) 0%, transparent 70%);
  filter: blur(72px);
  pointer-events: none;
  z-index: 0;
  animation: ${pulse} 8s ease-in-out infinite;
`;

/* HERO */
const Hero = styled.section`
  position: relative;
  min-height: 100svh;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  padding: 8rem 6vw 4rem;
  overflow: hidden;
  background:
    radial-gradient(ellipse 70% 80% at 72% 55%, rgba(76,175,80,0.1) 0%, transparent 65%),
    radial-gradient(ellipse 40% 50% at 15% 60%, rgba(76,175,80,0.06) 0%, transparent 60%),
    #f5faf5;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(76,175,80,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(76,175,80,0.06) 1px, transparent 1px);
    background-size: 48px 48px;
    pointer-events: none;
    z-index: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: flex-start;
    padding: 7rem 1.5rem 3rem;
    min-height: auto;
    gap: 2rem;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(100deg, rgba(245,250,245,0.92) 35%, rgba(245,250,245,0.1) 100%);
  pointer-events: none;
  z-index: 0;
`;

const HeroContent = styled.div`
  position: relative;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  z-index: 1;
  max-width: 480px;

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
    max-width: 100%;
  }
`;

const HeroImage = styled.img`
  position: relative;
  z-index: 1;
  width: clamp(480px, 54vw, 820px);
  height: auto;
  object-fit: contain;
  flex-shrink: 0;
  filter:
    drop-shadow(0 0 80px rgba(76,175,80,0.35))
    drop-shadow(0 0 20px rgba(76,175,80,0.2));

  @media (max-width: 768px) {
    width: 90%;
    max-width: 360px;
  }
`;

const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.28em;
  color: #4CAF50;
  text-transform: uppercase;

  &::before {
    content: "";
    display: inline-block;
    width: 24px;
    height: 2px;
    background: #4CAF50;
    border-radius: 2px;
  }
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.8rem, 5.5vw, 5rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: #111;

  @media (max-width: 600px) {
    font-size: clamp(1.3rem, 5.8vw, 2.2rem);
  }
`;

const HeroSub = styled.p`
  margin: 0;
  font-size: clamp(0.95rem, 1.4vw, 1.1rem);
  font-weight: 400;
  color: rgba(0,0,0,0.55);
  max-width: 420px;
  white-space: pre-line;
  line-height: 1.7;
  border-left: 2px solid rgba(76,175,80,0.5);
  padding-left: 1rem;

  @media (max-width: 768px) {
    max-width: 100%;
    border-left: none;
    padding-left: 0;
  }
`;

/* BUTTONS */
const GreenButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #4CAF50;
  color: #000;
  font-family: inherit;
  font-size: clamp(0.85rem, 1.2vw, 0.95rem);
  font-weight: 700;
  padding: 0.9rem 2.2rem;
  border: 2px solid #4CAF50;
  border-radius: 4px;
  cursor: pointer;
  letter-spacing: 0.04em;
  white-space: nowrap;
  box-shadow: 0 0 24px rgba(76,175,80,0.4), 0 4px 16px rgba(0,0,0,0.3);
  transition: background 0.2s, color 0.2s, box-shadow 0.2s, transform 0.15s;

  &:hover {
    background: transparent;
    color: #4CAF50;
    box-shadow: 0 0 24px rgba(76,175,80,0.35);
    transform: translateY(-1px);
  }

  &:active { transform: translateY(0); }
`;

const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: transparent;
  color: #4CAF50;
  font-family: inherit;
  font-size: clamp(0.85rem, 1.2vw, 1rem);
  font-weight: 600;
  padding: 0.85rem 2rem;
  border: 2px solid #4CAF50;
  border-radius: 2px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  letter-spacing: 0.02em;
  margin-top: 1rem;

  &:hover {
    background: #4CAF50;
    color: #000;
  }
`;

/* SECTIONS */
const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: 7rem 6vw;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const SectionEyebrow = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: #4CAF50;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 3.5rem;
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-weight: 700;
  line-height: 1.15;
`;

/* HOW IT WORKS */
const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const StepCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 2.5rem 1.5rem;
  border: 1px solid rgba(76,175,80,0.2);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;

  &:hover {
    border-color: rgba(76,175,80,0.5);
    box-shadow: 0 6px 24px rgba(76,175,80,0.12);
    transform: translateY(-4px);
  }
`;

const StepNum = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: #4CAF50;
`;

const StepImg = styled.img`
  width: clamp(80px, 15vw, 130px);
  object-fit: contain;
`;

const StepLabel = styled.div`
  font-size: 1.15rem;
  font-weight: 600;
`;

const StepDesc = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: rgba(0,0,0,0.6);
  line-height: 1.65;
`;

/* FEATURES */
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 1fr;
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }
`;

const FeatureCard = styled.div`
  padding: 2rem 1.5rem;
  border: 1px solid rgba(0,0,0,0.07);
  border-radius: 8px;
  background: #f8faf8;
  box-shadow: 0 1px 8px rgba(0,0,0,0.04);
  transition: border-color 0.25s, transform 0.25s, box-shadow 0.25s;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: rgba(76,175,80,0.4);
    box-shadow: 0 6px 24px rgba(76,175,80,0.1);
    transform: translateY(-4px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const FeatureDesc = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: rgba(0,0,0,0.55);
  line-height: 1.6;
`;

/* DOWNLOAD */
const CtaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5vw;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    text-align: center;
    align-items: center;
  }
`;

const CtaTitle = styled.h2`
  margin: 0.5rem 0 0.75rem;
  font-size: clamp(2.5rem, 6vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
`;

const CtaSub = styled.p`
  margin: 0 0 1.5rem;
  font-size: clamp(0.9rem, 1.4vw, 1.1rem);
  color: rgba(0,0,0,0.55);
`;

/* ABOUT */
const AboutRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5vw;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }
`;

const AboutTitle = styled.h2`
  margin: 0.5rem 0 1.25rem;
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 700;
  line-height: 1.15;
`;

const AboutText = styled.p`
  margin: 0;
  font-size: clamp(0.9rem, 1.3vw, 1.05rem);
  color: rgba(0,0,0,0.6);
  line-height: 1.75;
  max-width: 500px;
`;

/* About logo blob */
const AboutLogoWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AboutLogoBlob = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 115%;
  height: 115%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(76,175,80,0.3) 0%, transparent 70%);
  filter: blur(36px);
  animation: ${logoPulse} 6s ease-in-out infinite;
  pointer-events: none;
`;

export default Home;
